import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io, Socket } from 'socket.io-client';
import { ShotData } from '../types';
import { toast } from '@/hooks/use-toast';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3071';

interface ShotAnalysis {
  shotId: string;
  quality: number;
  technique: Record<string, number>;
  recommendations: string[];
}

interface RealtimeShotOptions {
  sessionId?: string;
  enabled?: boolean;
  onNewShot?: (shot: ShotData) => void;
  onShotAnalyzed?: (analysis: ShotAnalysis) => void;
  onBulkUpload?: (shots: ShotData[]) => void;
}

export const useRealtimeShots = ({
  sessionId,
  enabled = true,
  onNewShot,
  onShotAnalyzed,
  onBulkUpload
}: RealtimeShotOptions) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;

    // Connect to WebSocket server
    const socket: Socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      withCredentials: true, // Send cookies with the request
      auth: {
        // Add any authentication tokens if needed
      },
    });

    // Connection event handlers
    socket.on('connect', () => {
      console.log('Connected to WebSocket server');
      
      // Join session room if sessionId provided
      if (sessionId) {
        socket.emit('join:session', sessionId);
      }
    });

    socket.on('disconnect', () => {
      console.log('Disconnected from WebSocket server');
    });

    socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      // Only show toast for non-authentication errors
      if (error.message !== 'No session cookie' && error.message !== 'No session found') {
        toast({
          title: 'Connection Warning',
          description: 'Real-time updates may be limited. Some features will work in demo mode.',
        });
      }
    });

    // Shot event handlers
    socket.on('shot:new', (data: { shot: ShotData; sessionId: string }) => {
      console.log('New shot received:', data);
      
      // Update cache for session shots
      if (data.sessionId) {
        queryClient.setQueryData(
          ['shots', 'session', data.sessionId],
          (oldData: ShotData[] = []) => [data.shot, ...oldData]
        );
      }
      
      // Update cache for recent shots
      queryClient.setQueryData(
        ['shots', 'recent'],
        (oldData: ShotData[] = []) => [data.shot, ...oldData.slice(0, 49)]
      );

      // Update live shots cache
      queryClient.setQueryData(
        ['shots', 'live', data.sessionId],
        (oldData: ShotData[] = []) => [data.shot, ...oldData]
      );

      // Trigger callback if provided
      if (onNewShot) {
        onNewShot(data.shot);
      }

      // Show notification
      toast({
        title: 'New Shot Recorded',
        description: `Shot quality: ${data.shot.quality || 'Unknown'}`,
      });
    });

    socket.on('shot:analyzed', (data: { shotId: string; analysis: ShotAnalysis }) => {
      console.log('Shot analysis received:', data);
      
      // Update shot in cache with analysis data
      queryClient.setQueryData(
        ['shots', 'metrics', data.shotId],
        data.analysis
      );

      // Trigger callback if provided
      if (onShotAnalyzed) {
        onShotAnalyzed(data.analysis);
      }

      // Show notification
      toast({
        title: 'Shot Analysis Complete',
        description: `Analysis for shot ${data.shotId} is ready`,
      });
    });

    socket.on('shots:bulk-uploaded', (data: { shots: ShotData[]; sessionId: string }) => {
      console.log('Bulk shots uploaded:', data);
      
      // Update cache for session shots
      if (data.sessionId) {
        queryClient.setQueryData(
          ['shots', 'session', data.sessionId],
          (oldData: ShotData[] = []) => [...data.shots, ...oldData]
        );
      }

      // Trigger callback if provided
      if (onBulkUpload) {
        onBulkUpload(data.shots);
      }

      // Show notification
      toast({
        title: 'Bulk Upload Complete',
        description: `${data.shots.length} shots uploaded successfully`,
      });
    });

    // Session-specific events
    if (sessionId) {
      socket.on(`session:${sessionId}:update`, (data: { type: string; payload?: unknown }) => {
        console.log('Session update received:', data);

        // Invalidate session queries to refetch latest data
        queryClient.invalidateQueries({ queryKey: ['shots', 'session', sessionId] });
        queryClient.invalidateQueries({ queryKey: ['sessions', sessionId] });
      });

      socket.on(`session:${sessionId}:player-joined`, (data: { playerId: string; playerName: string }) => {
        toast({
          title: 'Player Joined',
          description: `${data.playerName} joined the session`,
        });
      });

      socket.on(`session:${sessionId}:player-left`, (data: { playerId: string; playerName: string }) => {
        toast({
          title: 'Player Left',
          description: `${data.playerName} left the session`,
        });
      });
    }

    // Cleanup on unmount
    return () => {
      if (sessionId) {
        socket.emit('leave:session', sessionId);
      }
      socket.disconnect();
    };
  }, [enabled, sessionId, queryClient, onNewShot, onShotAnalyzed, onBulkUpload]);

  // Emit shot event
  const emitShot = useCallback((shot: ShotData) => {
    const socket = io(SOCKET_URL);
    socket.emit('shot:create', { shot, sessionId });
  }, [sessionId]);

  // Emit analysis request
  const requestAnalysis = useCallback((shotId: string) => {
    const socket = io(SOCKET_URL);
    socket.emit('shot:analyze', { shotId, sessionId });
  }, [sessionId]);

  // Emit bulk upload
  const emitBulkUpload = useCallback((shots: ShotData[]) => {
    const socket = io(SOCKET_URL);
    socket.emit('shots:bulk-upload', { shots, sessionId });
  }, [sessionId]);

  return {
    emitShot,
    requestAnalysis,
    emitBulkUpload,
  };
};

// Hook for live session monitoring
export const useLiveSessionMonitor = (sessionId: string | undefined) => {
  const handleNewShot = useCallback((shot: ShotData) => {
    // Update visualization store with new shot
    interface ShotVisualizationStore {
      getState: () => {
        loadShot: (shot: ShotData) => void;
        setAnimation: (params: { isPlaying: boolean; currentTime: number }) => void;
      };
    }
    const store = (window as unknown as { __shotVisualizationStore?: ShotVisualizationStore }).__shotVisualizationStore;
    if (store) {
      store.getState().loadShot(shot);
      store.getState().setAnimation({ isPlaying: true, currentTime: 0 });
    }
  }, []);

  const handleShotAnalyzed = useCallback((analysis: ShotAnalysis) => {
    // Update visualization with analysis
    console.log('Shot analysis received for visualization:', analysis);
  }, []);

  const { emitShot, requestAnalysis, emitBulkUpload } = useRealtimeShots({
    sessionId,
    enabled: !!sessionId,
    onNewShot: handleNewShot,
    onShotAnalyzed: handleShotAnalyzed,
  });

  return {
    emitShot,
    requestAnalysis,
    emitBulkUpload,
  };
};